import { useState } from "react";
import {
  Building2, Phone, Mail, MapPin, CalendarCheck, ShieldCheck,
  Clock, CheckCircle, AlertTriangle, ExternalLink, MessageCircle,
  Users, Star, Bell, FileText, CreditCard, ChevronRight, Megaphone,
  Send, FileDown, IdCard, AlertCircle, Banknote
} from "lucide-react";

// ── Données simulées (à remplacer par API quand disponible) ──────────────────

const SYNDICAT = {
  nom:        "Secteur des Pharmaciens de Thiès",
  code:       "SPT-001",
  zone:       "Région de Thiès",
  communes:   ["Thiès Nord", "Thiès Sud", "Thiès Est", "Thiès Ouest", "Mbour", "Tivaouane", "Mboro", "Mékhé"],
  nbMembres:  44,
  nbCommunes: 8,
  gardesMois: 3,
  president: {
    nom:       "Dr. DIOP Mamadou",
    titre:     "Président du Secteur",
    telephone: "+221 77 100 11 11",
    email:     "president.spt@secteur.sn",
  },
  secretaire: {
    nom:       "Dr. FALL Ibrahima",
    titre:     "Secrétaire Général",
    telephone: "+221 77 100 22 22",
    email:     "secretariat.spt@secteur.sn",
  },
};

const MES_GARDES = [
  { date: "Vendredi 18 juillet 2026",  type: "Nuit",    horaire: "20h – 8h",  statut: "confirmee" },
  { date: "Dimanche 3 août 2026",      type: "Journée", horaire: "8h – 20h",  statut: "en_attente" },
  { date: "Mercredi 20 août 2026",     type: "Nuit",    horaire: "20h – 8h",  statut: "confirmee" },
];

const ACTUALITES = [
  {
    type:  "reunion",
    titre: "Réunion mensuelle du secteur",
    date:  "25 juillet 2026 – 10h00",
    lieu:  "Salle de conférence – Mairie de Thiès",
    badge: "Réunion",
    color: "blue",
  },
  {
    type:  "cotisation",
    titre: "Rappel : Cotisation trimestrielle T3",
    date:  "Avant le 31 juillet 2026",
    lieu:  "Virement ou paiement en main propre",
    badge: "Finance",
    color: "amber",
  },
  {
    type:  "circulaire",
    titre: "Circulaire DPM – Médicaments essentiels",
    date:  "Publiée le 10 juillet 2026",
    lieu:  "Direction de la Pharmacie et du Médicament",
    badge: "Circulaire",
    color: "purple",
  },
];

const MA_COTISATION = {
  statut:        "A_JOUR",   // A_JOUR | EN_RETARD | NON_PAYE
  montantAnnuel: 25000,
  dernierPaiement: "15 janvier 2026",
  prochaineEcheance: "15 janvier 2027",
  historique: [
    { periode: "2026", montant: 25000, date: "15 janv. 2026", statut: "paye" },
    { periode: "2025", montant: 20000, date: "10 janv. 2025", statut: "paye" },
    { periode: "2024", montant: 20000, date: "05 janv. 2024", statut: "paye" },
  ],
};

const MES_DOCUMENTS = [
  { nom: "Licence d'exploitation",        type: "PDF", date: "12 mars 2022",    icon: IdCard    },
  { nom: "Carte de membre — Secteur SPT", type: "PDF", date: "15 janv. 2026",   icon: IdCard    },
  { nom: "Attestation d'abonnement",      type: "PDF", date: "15 juil. 2026",   icon: FileDown  },
  { nom: "Circulaire DPM — Juil. 2026",   type: "PDF", date: "10 juil. 2026",   icon: FileText  },
];

const MON_ABONNEMENT = {
  plan:         "PREMIUM",
  statut:       "ACTIF",
  montant:      15000,
  dateDebut:    "15 juillet 2026",
  dateFin:      "15 juillet 2027",
  compteVerifie: true,
  valideOrdre:   true,
};

// ── Composants utilitaires ───────────────────────────────────────────────────

const StatCard = ({ icon: Icon, value, label, color = "emerald" }) => {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    blue:    "bg-blue-50    text-blue-600    border-blue-100",
    amber:   "bg-amber-50   text-amber-600   border-amber-100",
    purple:  "bg-purple-50  text-purple-600  border-purple-100",
  };
  return (
    <div className={`rounded-2xl border p-4 text-center ${colors[color]}`}>
      <div className="flex justify-center mb-2">
        <Icon size={20} />
      </div>
      <p className="text-2xl font-black">{value}</p>
      <p className="text-xs font-medium opacity-80 mt-0.5">{label}</p>
    </div>
  );
};

const PersonneCard = ({ personne }) => (
  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
    <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
      {personne.nom.charAt(personne.nom.indexOf(" ") + 1)}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-bold text-gray-900 text-sm">{personne.nom}</p>
      <p className="text-xs text-emerald-600 font-medium mb-2">{personne.titre}</p>
      <div className="space-y-1">
        <a href={`tel:${personne.telephone}`}
           className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-emerald-600">
          <Phone size={11} /> {personne.telephone}
        </a>
        <a href={`mailto:${personne.email}`}
           className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-emerald-600">
          <Mail size={11} /> {personne.email}
        </a>
      </div>
    </div>
  </div>
);

const GardeRow = ({ garde }) => {
  const confirmed = garde.statut === "confirmee";
  return (
    <div className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${confirmed ? "bg-emerald-500" : "bg-amber-400"}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">{garde.date}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {garde.type} · {garde.horaire}
        </p>
      </div>
      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
        confirmed
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : "bg-amber-50 text-amber-700 border border-amber-200"
      }`}>
        {confirmed ? "Confirmée" : "En attente"}
      </span>
    </div>
  );
};

const badgeColors = {
  blue:   "bg-blue-100 text-blue-700",
  amber:  "bg-amber-100 text-amber-700",
  purple: "bg-purple-100 text-purple-700",
};

const ActualiteCard = ({ item }) => (
  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
    <div className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${badgeColors[item.color]}`}>
      {item.badge}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900">{item.titre}</p>
      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
        <Clock size={10} /> {item.date}
      </p>
      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
        <MapPin size={10} /> {item.lieu}
      </p>
    </div>
    <ChevronRight size={15} className="text-gray-300 flex-shrink-0 mt-1" />
  </div>
);

// ── Page principale ──────────────────────────────────────────────────────────

export default function MonSyndicat() {
  const [activeTab, setActiveTab]     = useState("syndicat");
  const [msgTexte, setMsgTexte]       = useState("");
  const [msgEnvoye, setMsgEnvoye]     = useState(false);

  const tabs = [
    { id: "syndicat",   label: "Mon Secteur"  },
    { id: "gardes",     label: "Mes Gardes"   },
    { id: "cotisation", label: "Cotisation"   },
    { id: "abonnement", label: "Abonnement"   },
  ];

  const handleEnvoyerMsg = () => {
    if (!msgTexte.trim()) return;
    setMsgEnvoye(true);
    setMsgTexte("");
    setTimeout(() => setMsgEnvoye(false), 4000);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* ── Header banner ────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-emerald-700 to-teal-600 rounded-2xl p-6 mb-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%">
            <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="white"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#dots)"/>
          </svg>
        </div>
        <div className="relative z-10 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 size={20} className="opacity-80" />
              <span className="text-xs font-semibold opacity-70 uppercase tracking-wider">Votre secteur</span>
            </div>
            <h1 className="text-xl font-black leading-tight">{SYNDICAT.nom}</h1>
            <p className="text-sm opacity-75 mt-1 flex items-center gap-1.5">
              <MapPin size={13} /> {SYNDICAT.zone} · Code {SYNDICAT.code}
            </p>
          </div>
          <div className="flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-full flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
            <span className="text-xs font-semibold">Actif</span>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="relative z-10 grid grid-cols-3 gap-3 mt-5">
          {[
            { v: SYNDICAT.nbMembres,  l: "Membres"   },
            { v: SYNDICAT.nbCommunes, l: "Communes"  },
            { v: SYNDICAT.gardesMois, l: "Gardes/mois" },
          ].map(({ v, l }) => (
            <div key={l} className="bg-white/15 rounded-xl p-3 text-center backdrop-blur-sm">
              <p className="text-2xl font-black">{v}</p>
              <p className="text-xs opacity-75 font-medium">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Onglets ──────────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
              activeTab === t.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Onglet : Mon Secteur ─────────────────────────────────────────── */}
      {activeTab === "syndicat" && (
        <div className="space-y-5">

          {/* Direction */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <Star size={15} className="text-emerald-600" /> Direction du Secteur
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PersonneCard personne={SYNDICAT.president} />
              <PersonneCard personne={SYNDICAT.secretaire} />
            </div>
          </div>

          {/* Zones couvertes */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <MapPin size={15} className="text-emerald-600" /> Zones couvertes
            </h2>
            <div className="flex flex-wrap gap-2">
              {SYNDICAT.communes.map(c => (
                <span key={c} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full text-xs font-semibold">
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Actualités */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <Megaphone size={15} className="text-emerald-600" /> Actualités & Circulaires
            </h2>
            <div className="space-y-3">
              {ACTUALITES.map((item, i) => <ActualiteCard key={i} item={item} />)}
            </div>
          </div>

          {/* Messagerie directe secteur */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-1">
              <Send size={15} className="text-emerald-600" /> Écrire au Secrétariat
            </h2>
            <p className="text-xs text-gray-400 mb-4">Message transmis directement au secrétariat de votre secteur</p>
            {msgEnvoye && (
              <div className="flex items-center gap-2 mb-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                <CheckCircle size={15} className="text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Message envoyé au secrétariat.</span>
              </div>
            )}
            <textarea
              value={msgTexte}
              onChange={e => setMsgTexte(e.target.value)}
              placeholder="Ex : Je serai absent pour ma garde du 18 juillet..."
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 mb-3"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">{msgTexte.length}/500 caractères</p>
              <button
                onClick={handleEnvoyerMsg}
                disabled={!msgTexte.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 transition-colors"
              >
                <Send size={13} /> Envoyer
              </button>
            </div>
          </div>

          {/* Contact SunuFarmasi */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <Phone size={15} className="text-emerald-600" /> Contact SunuFarmasi
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href="https://wa.me/221774792457" target="_blank" rel="noreferrer"
                 className="flex items-center gap-3 p-3.5 bg-emerald-50 border border-emerald-100 rounded-xl hover:bg-emerald-100 transition-colors group">
                <div className="w-9 h-9 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={17} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">WhatsApp</p>
                  <p className="text-xs text-gray-500">+221 77 479 24 57</p>
                </div>
                <ExternalLink size={14} className="text-gray-400 ml-auto group-hover:text-emerald-600" />
              </a>
              <a href="mailto:sunufarmasi@gmail.com"
                 className="flex items-center gap-3 p-3.5 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100 transition-colors group">
                <div className="w-9 h-9 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={17} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Email support</p>
                  <p className="text-xs text-gray-500">sunufarmasi@gmail.com</p>
                </div>
                <ExternalLink size={14} className="text-gray-400 ml-auto group-hover:text-gray-600" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Onglet : Mes Gardes ──────────────────────────────────────────── */}
      {activeTab === "gardes" && (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-1">
              <CalendarCheck size={15} className="text-emerald-600" /> Prochaines gardes
            </h2>
            <p className="text-xs text-gray-400 mb-4">Planning publié par votre secteur</p>
            <div>
              {MES_GARDES.map((g, i) => <GardeRow key={i} garde={g} />)}
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-start gap-3">
            <Bell size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">Vous avez un empêchement ?</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Signalez-le à votre secteur via WhatsApp ou en allant dans l'onglet "Horaires & Garde".
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <FileText size={15} className="text-emerald-600" /> Historique — Juin 2026
            </h2>
            <div className="space-y-0">
              {[
                { date: "Samedi 7 juin 2026",   type: "Journée", horaire: "8h – 20h",  statut: "realisee" },
                { date: "Jeudi 19 juin 2026",   type: "Nuit",    horaire: "20h – 8h",  statut: "realisee" },
              ].map((g, i) => (
                <div key={i} className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-300 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-700">{g.date}</p>
                    <p className="text-xs text-gray-400">{g.type} · {g.horaire}</p>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                    Réalisée
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Onglet : Cotisation & Documents ─────────────────────────────── */}
      {activeTab === "cotisation" && (
        <div className="space-y-5">

          {/* Statut cotisation */}
          {MA_COTISATION.statut === "EN_RETARD" && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-800 text-sm">Cotisation en retard</p>
                <p className="text-xs text-red-600 mt-0.5">Votre cotisation est due. Sans paiement, votre pharmacie peut être suspendue du secteur.</p>
              </div>
            </div>
          )}

          <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-semibold opacity-70 uppercase tracking-wider mb-1">Cotisation annuelle</p>
                <p className="text-3xl font-black">{MA_COTISATION.montantAnnuel.toLocaleString("fr-FR")} FCFA</p>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 ${
                MA_COTISATION.statut === "A_JOUR"
                  ? "bg-white/20"
                  : "bg-red-400/80"
              }`}>
                <span className={`w-2 h-2 rounded-full ${MA_COTISATION.statut === "A_JOUR" ? "bg-emerald-300 animate-pulse" : "bg-white"}`} />
                {MA_COTISATION.statut === "A_JOUR" ? "À jour" : "En retard"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
              <div>
                <p className="text-xs opacity-60">Dernier paiement</p>
                <p className="text-sm font-semibold">{MA_COTISATION.dernierPaiement}</p>
              </div>
              <div>
                <p className="text-xs opacity-60">Prochaine échéance</p>
                <p className="text-sm font-semibold">{MA_COTISATION.prochaineEcheance}</p>
              </div>
            </div>
          </div>

          {/* Historique paiements */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <Banknote size={15} className="text-emerald-600" /> Historique des cotisations
            </h2>
            <div className="space-y-0">
              {MA_COTISATION.historique.map((h, i) => (
                <div key={i} className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={15} className="text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">Cotisation {h.periode}</p>
                    <p className="text-xs text-gray-500">Payée le {h.date}</p>
                  </div>
                  <span className="text-sm font-bold text-gray-800">{h.montant.toLocaleString("fr-FR")} FCFA</span>
                </div>
              ))}
            </div>
          </div>

          {/* Mes Documents */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <FileDown size={15} className="text-emerald-600" /> Mes Documents
            </h2>
            <div className="space-y-2">
              {MES_DOCUMENTS.map((doc, i) => (
                <div key={i} className="flex items-center gap-3 p-3.5 bg-gray-50 border border-gray-100 rounded-xl hover:border-emerald-200 hover:bg-emerald-50/50 transition-colors cursor-pointer group">
                  <div className="w-9 h-9 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:border-emerald-200">
                    <doc.icon size={16} className="text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{doc.nom}</p>
                    <p className="text-xs text-gray-400">{doc.type} · {doc.date}</p>
                  </div>
                  <FileDown size={15} className="text-gray-300 group-hover:text-emerald-500 flex-shrink-0" />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 text-center mt-3">
              Pour tout document manquant, contactez le secrétariat de votre secteur.
            </p>
          </div>
        </div>
      )}

      {/* ── Onglet : Abonnement ──────────────────────────────────────────── */}
      {activeTab === "abonnement" && (
        <div className="space-y-5">

          {/* Plan */}
          <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-semibold opacity-70 uppercase tracking-wider mb-1">Plan actuel</p>
                <p className="text-3xl font-black">{MON_ABONNEMENT.plan}</p>
              </div>
              <span className="bg-white/20 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                Actif
              </span>
            </div>
            <p className="text-2xl font-bold mb-1">
              {MON_ABONNEMENT.montant.toLocaleString("fr-FR")} <span className="text-base font-medium opacity-80">FCFA / an</span>
            </p>
            <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs opacity-60">Début</p>
                <p className="text-sm font-semibold">{MON_ABONNEMENT.dateDebut}</p>
              </div>
              <div>
                <p className="text-xs opacity-60">Expiration</p>
                <p className="text-sm font-semibold">{MON_ABONNEMENT.dateFin}</p>
              </div>
            </div>
          </div>

          {/* Statut vérifications */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <ShieldCheck size={15} className="text-emerald-600" /> Statut du compte
            </h2>
            <div className="space-y-3">
              {[
                { label: "Compte vérifié par SunuFarmasi",  ok: MON_ABONNEMENT.compteVerifie },
                // { label: "Validé par l'Ordre des Pharmaciens", ok: MON_ABONNEMENT.valideOrdre },
                { label: "Abonnement en cours de validité",  ok: MON_ABONNEMENT.statut === "ACTIF" },
                { label: "Pharmacie visible sur l'application", ok: true },
              ].map(({ label, ok }) => (
                <div key={label} className={`flex items-center gap-3 p-3.5 rounded-xl border ${
                  ok ? "bg-emerald-50 border-emerald-100" : "bg-red-50 border-red-100"
                }`}>
                  {ok
                    ? <CheckCircle size={17} className="text-emerald-600 flex-shrink-0" />
                    : <AlertTriangle size={17} className="text-red-500 flex-shrink-0" />
                  }
                  <span className={`text-sm font-medium ${ok ? "text-emerald-800" : "text-red-700"}`}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Ce qui est inclus */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <CreditCard size={15} className="text-emerald-600" /> Ce qui est inclus
            </h2>
            <div className="space-y-2">
              {[
                "Visibilité GPS sur l'application SunuFarmasi",
                "Gestion des gardes (confirmer / décliner)",
                "Tableau de bord et statistiques",
                "Gestion des employés",
                "Support WhatsApp prioritaire",
                "Mises à jour automatiques de la plateforme",
              ].map(item => (
                <div key={item} className="flex items-center gap-2.5">
                  <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-2">Pour renouveler ou modifier votre abonnement</p>
            <a href="https://wa.me/221774792457" target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors">
              <MessageCircle size={15} />
              Contacter SunuFarmasi
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
